/**
 * Deltakerne i kalenderen med navn og personfarge (#157, steg 7a).
 *
 * Hendelsene har bare `attendee_ids`. Navn og farge kommer fra
 * /api/members/basic (`useFamilyMembers`), samme kilde som filterchipsene på
 * Oppgaver. Et medlem som ikke er i lista (arkivert, eller lista feilet),
 * hoppes over: avatarene er en forfining, og hendelsen vises uansett.
 */
import { useMemo } from 'react';
import { isSameDay } from 'date-fns';
import { asColorSlot } from '../../components/primitives/InitialAvatar';
import { resolveStripeSlot, type ColorStripeSlot } from '../../components/primitives/EventColorStripe';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import type { EventAttendee } from './EventCard';
import type { EventInstance } from './useCalendarRange';

export type AttendeeLookup = ReadonlyMap<string, EventAttendee>;

/** Ingen kjente deltakere: hendelsene vises uten avatarer. */
export const NO_ATTENDEES: AttendeeLookup = new Map();

/** Medlemmene som oppslag fra id til navn (kortnavn først) og farge. */
export function useAttendeeLookup(): AttendeeLookup {
  const { data } = useFamilyMembers();
  return useMemo(() => {
    if (!data || data.length === 0) return NO_ATTENDEES;
    return new Map(
      data.map((m) => [m.id, { name: m.short_name || m.name, colorSlot: asColorSlot(m.color_token) }]),
    );
  }, [data]);
}

export function attendeesOf(ev: EventInstance, lookup: AttendeeLookup): EventAttendee[] {
  return ev.attendee_ids
    .map((id) => lookup.get(id))
    .filter((a): a is EventAttendee => a !== undefined);
}

/** Hendelsene som starter på `day`, i lokal tid, som visningene alltid har gjort. */
export function eventsOn(events: readonly EventInstance[], day: Date): EventInstance[] {
  return events.filter((e) => isSameDay(new Date(e.starts_at), day));
}

/**
 * Prikkene for en dag i ukestripen: én per person med noe den dagen, i
 * rekkefølgen hendelsene kommer. En hendelse uten kjente deltakere gir
 * hendelsens egen farge.
 */
export function dayDots(
  dayEvents: readonly EventInstance[],
  lookup: AttendeeLookup,
): ColorStripeSlot[] {
  const seen = new Set<string>();
  const dots: ColorStripeSlot[] = [];
  for (const ev of dayEvents) {
    let known = false;
    for (const id of ev.attendee_ids) {
      const person = lookup.get(id);
      if (!person) continue;
      known = true;
      if (seen.has(id)) continue;
      seen.add(id);
      dots.push(person.colorSlot);
    }
    if (!known) {
      // Nøkkelen er den løste fargen, så ukjente tokens ikke gir flere like prikker.
      const slot = resolveStripeSlot(ev.color_token);
      const key = `event:${slot}`;
      if (!seen.has(key)) {
        seen.add(key);
        dots.push(slot);
      }
    }
  }
  return dots;
}
